import React, { useEffect, useRef, useState } from "react";
import { Mic, Search, Loader2, X } from "lucide-react";
import { api } from "../lib/api";
import { AISearchResults } from "./AISearchResults";

/**
 * Unified AI Search + Push-to-Talk voice bar.
 * - Text input: "Τι θέλεις να φας;" → POST /api/ai/search
 * - Mic (press & hold): records audio, on release → POST /api/ai/voice-search
 * - Both endpoints return the same shape consumed by AISearchResults.
 */
export const AISearchBar = () => {
    const [query, setQuery] = useState("");
    const [phase, setPhase] = useState("idle"); // idle | recording | processing | result
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [showResults, setShowResults] = useState(false);

    // recording refs
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(null);
    const startedAtRef = useRef(0);
    const cancelledRef = useRef(false);
    const safetyTimerRef = useRef(null);

    const [waveform, setWaveform] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);

    useEffect(() => () => cleanupRecorder(), []);

    const cleanupRecorder = () => {
        try { if (rafRef.current) cancelAnimationFrame(rafRef.current); } catch {}
        try { if (audioCtxRef.current && audioCtxRef.current.state !== "closed") audioCtxRef.current.close(); } catch {}
        try { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
        try { if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current); } catch {}
        audioCtxRef.current = null;
        analyserRef.current = null;
        streamRef.current = null;
        rafRef.current = null;
        safetyTimerRef.current = null;
    };

    const animateWaveform = () => {
        const analyser = analyserRef.current;
        if (!analyser) return;
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
            analyser.getByteFrequencyData(data);
            const bars = 12;
            const step = Math.floor(data.length / bars);
            const next = [];
            for (let i = 0; i < bars; i++) {
                let sum = 0;
                for (let j = 0; j < step; j++) sum += data[i * step + j] || 0;
                next.push(Math.min(100, (sum / step / 255) * 140));
            }
            setWaveform(next);
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
    };

    const startRecording = async () => {
        if (phase === "recording" || phase === "processing") return;
        setError("");
        cancelledRef.current = false;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            // waveform analyser
            try {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                const ctx = new Ctx();
                audioCtxRef.current = ctx;
                const source = ctx.createMediaStreamSource(stream);
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 64;
                source.connect(analyser);
                analyserRef.current = analyser;
                animateWaveform();
            } catch {}

            const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                ? "audio/webm;codecs=opus"
                : "audio/webm";
            const mr = new MediaRecorder(stream, { mimeType: mime });
            chunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            mr.onstop = async () => {
                const blob = new Blob(chunksRef.current, { type: mime });
                cleanupRecorder();
                if (cancelledRef.current) { setPhase("idle"); return; }
                const dur = Date.now() - startedAtRef.current;
                if (blob.size < 1500 || dur < 350) {
                    setError("Δεν άκουσα κάτι, δοκίμασε ξανά");
                    setPhase("idle");
                    return;
                }
                await sendVoice(blob);
            };
            mr.start();
            mediaRecorderRef.current = mr;
            startedAtRef.current = Date.now();
            setPhase("recording");

            // hard safety stop after 20s
            safetyTimerRef.current = setTimeout(() => {
                if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
                    try { mediaRecorderRef.current.stop(); } catch {}
                }
            }, 20000);
        } catch (err) {
            setError("Δώσε πρόσβαση στο μικρόφωνο");
            setPhase("idle");
            cleanupRecorder();
        }
    };

    const stopRecording = (cancel = false) => {
        if (phase !== "recording") return;
        cancelledRef.current = !!cancel;
        const mr = mediaRecorderRef.current;
        if (mr && mr.state === "recording") {
            try { mr.stop(); } catch {}
        }
        if (!cancel) setPhase("processing");
        else { setPhase("idle"); cleanupRecorder(); }
    };

    const sendVoice = async (blob) => {
        setPhase("processing");
        try {
            const fd = new FormData();
            fd.append("audio", blob, "search.webm");
            const { data } = await api.post(
                `/ai/voice-search?session_id=v_${Date.now()}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 }
            );
            setResult(data);
            setQuery(data.transcript || "");
            setPhase("result");
            setShowResults(true);
        } catch (err) {
            setError(err.response?.data?.detail || "Σφάλμα φωνής, δοκίμασε ξανά");
            setPhase("idle");
        }
    };

    const submitText = async () => {
        const q = query.trim();
        if (!q || phase === "processing") return;
        setError("");
        setPhase("processing");
        try {
            const { data } = await api.post(`/ai/search`, { query: q, session_id: `t_${Date.now()}` }, { timeout: 25000 });
            setResult(data);
            setPhase("result");
            setShowResults(true);
        } catch (err) {
            setError(err.response?.data?.detail || "Δοκίμασε ξανά");
            setPhase("idle");
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") submitText();
    };

    const recording = phase === "recording";
    const processing = phase === "processing";

    return (
        <>
            <div data-testid="ai-search-bar" className="relative">
                <div
                    className={`flex items-center gap-2 h-12 rounded-full px-4 glass-gold border transition ${
                        recording ? "border-[var(--gold)] shadow-[0_0_30px_rgba(244,179,57,0.5)]" : "border-[var(--gold)]/25"
                    }`}
                >
                    <Search size={15} className="text-[var(--gold-light)] shrink-0" />
                    <input
                        data-testid="ai-search-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Τι θέλεις να φας;"
                        disabled={recording || processing}
                        className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/40 text-white/90"
                    />
                    {query && !recording && !processing && (
                        <button
                            onClick={() => { setQuery(""); setResult(null); }}
                            className="h-6 w-6 grid place-items-center rounded-full hover:bg-white/10 text-white/50"
                            data-testid="ai-search-clear"
                        >
                            <X size={12} />
                        </button>
                    )}
                    {processing ? (
                        <div className="h-9 w-9 grid place-items-center rounded-full bg-[var(--gold)]/15">
                            <Loader2 size={15} className="animate-spin text-[var(--gold-light)]" />
                        </div>
                    ) : (
                        <button
                            data-testid="ai-search-mic"
                            aria-label="Push to talk"
                            onMouseDown={(e) => { e.preventDefault(); startRecording(); }}
                            onMouseUp={() => stopRecording(false)}
                            onMouseLeave={() => recording && stopRecording(false)}
                            onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
                            onTouchEnd={(e) => { e.preventDefault(); stopRecording(false); }}
                            className={`h-9 w-9 grid place-items-center rounded-full transition select-none touch-none ${
                                recording
                                    ? "bg-[var(--gold)] text-[#0B0B0F] scale-110 shadow-[0_0_24px_var(--gold)]"
                                    : "bg-[var(--gold)]/15 text-[var(--gold-light)] hover:bg-[var(--gold)]/25"
                            }`}
                        >
                            <Mic size={15} strokeWidth={2.4} />
                        </button>
                    )}
                </div>
                {error && (
                    <p data-testid="ai-search-error" className="absolute left-4 top-full mt-1 text-[10px] text-[var(--gold-light)]">
                        {error}
                    </p>
                )}
            </div>

            {/* PUSH-TO-TALK FULLSCREEN OVERLAY */}
            {recording && (
                <div data-testid="ptt-overlay" className="fixed inset-0 z-[800] grid place-items-center bg-black/90 backdrop-blur-md animate-fade-up">
                    <div className="text-center px-8 max-w-md">
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[var(--gold-light)]/70">// listening</p>
                        <h2 className="text-3xl mt-2 font-heading text-white" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                            Μίλα τώρα<span className="gold-text animate-pulse">...</span>
                        </h2>

                        {/* Mic + ring pulse */}
                        <div className="relative mx-auto mt-8 h-36 w-36 grid place-items-center">
                            <div className="absolute inset-0 rounded-full bg-[var(--gold)]/15 animate-ping" />
                            <div className="absolute inset-3 rounded-full bg-[var(--gold)]/10 animate-ping" style={{ animationDelay: "0.4s" }} />
                            <div className="relative h-28 w-28 grid place-items-center rounded-full bg-gradient-to-br from-[var(--gold)] to-[#c98e15] shadow-[0_0_60px_rgba(244,179,57,0.7)]">
                                <Mic size={44} strokeWidth={2.4} className="text-[#0B0B0F]" />
                            </div>
                        </div>

                        {/* Waveform */}
                        <div className="flex items-end justify-center gap-1.5 h-14 mt-6">
                            {waveform.map((h, i) => (
                                <div
                                    key={i}
                                    className="w-1.5 rounded-full bg-gradient-to-t from-[var(--gold)] to-[var(--gold-light)] transition-all duration-75"
                                    style={{ height: `${Math.max(8, h)}%` }}
                                />
                            ))}
                        </div>

                        <p className="mt-6 text-xs font-mono uppercase tracking-[0.3em] text-white/50">
                            άσε το κουμπί για να σταματήσεις
                        </p>
                    </div>
                </div>
            )}

            {/* AI Result panel */}
            <AISearchResults
                open={showResults && phase === "result" && !!result}
                data={result}
                onClose={() => { setShowResults(false); setPhase("idle"); }}
                onAgain={() => { setShowResults(false); setPhase("idle"); setQuery(""); setResult(null); }}
            />
        </>
    );
};
